import React, { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// MUI
import { Card, CardActions, CardContent, Toolbar } from '@mui/material';

// MUI Icons
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import DataArrayIcon from '@mui/icons-material/DataArray';

// Custom
import FormContent, { FormContentMode } from '@/components/FormContent';
import DataController from '@/lib/DataController';
import { CardHeading, CardToolbarButtonBack, CardToolbarMenuButton, CardToolbarMenuItemDelete, CardActionButtonSave } from '@/ui/Card';
import { FormFillContent, FormHeading, FormHeadingID, FormTab, FormTabPanel, FormTabs } from '@/ui/Form';
import { GridContainer, GridItem } from '@/ui/Grid';
import ModelTable from '@/ui/Table/ModelTable';
import useFormController from '@/components/useFormController';
import SnackbarContext, { SnackbarContextType } from '@/context/SnackbarContext/SnackbarContext';
import DynamicFormsModelFields from '@/models/dynamic_forms_model_fields';
import DialogContext, { DialogContextType } from '@/context/DialogContext/DialogContext';
import DynamicFormsAdminFieldsDialog from './DynamicFormsAdminFieldsDialog';

// Types
import { DCRecord } from '@/@types/lib/dataController';

interface IDynamicFormFormProps {
  dc: DataController;
  recordId: number;
  mode: FormContentMode;
  form: string;
}

const DynamicFormForm = (props: IDynamicFormFormProps) => {
  const snackbarContext = useContext(SnackbarContext) as SnackbarContextType;
  const navigate = useNavigate();
  const dialogContext = useContext(DialogContext) as DialogContextType;

  const [currentTab, setCurrentTab] = useState(0);
  const [dynamicFormFieldsRecords, setDynamicFormFieldsRecords] = useState<Array<DCRecord>>();

  const { t } = useTranslation();

  const { dc, recordId, mode, form } = props;
  const {
    record,
    validation,
    fields,
    onFieldChange,
    doClose,
    pickerDataControllers,
    dataChanged,
  } = useFormController({
    mode,
    recordId,
    form,
    dc,
    onClose: () => { },
    pickerApiPathParams: {
      dynamic_form_id: recordId,
    },
  });

  const dcDynamicFormFields = new DataController(DynamicFormsModelFields, `system/forms/${String(recordId)}/fields`);

  const title = record.title_ttoken ? (record.title_ttoken as string) : undefined;

  function refreshDynamicFormFieldsRecords() {
    dcDynamicFormFields
      .GetData()
      .then((resp) => {
        if (resp.success && resp.data && Array.isArray(resp.data) && resp.data.length > 0) {
          setDynamicFormFieldsRecords(resp.data);
        }
      })
      .catch((resp) => {
        console.log(resp);
      })
      .finally(() => { });
  }

  useEffect(() => {
    refreshDynamicFormFieldsRecords();
  }, []);

  const handleRowActionField = (
    action: string,
    id: number,
    rec?: { [key: string]: any }
  ) => {
    if (action === 'edit') {
      dialogContext.showDialog(DynamicFormsAdminFieldsDialog, {
        mode: 'update',
        form: 'update',
        dataId: id,
        onClose: () => {
          refreshDynamicFormFieldsRecords();
        },
        refreshLayers: () => { },
        initialRecord: rec || undefined,
        modelId: rec,
      });
    }
  };

  const openAddDialogField = () => {
    dialogContext.showDialog(DynamicFormsAdminFieldsDialog, {
      mode: 'insert',
      form: 'insert',
      dataId: undefined,
      onClose: () => {
        refreshDynamicFormFieldsRecords();
      },
      refreshLayers: () => { },
      initialRecord: undefined,
      modelId: recordId,
    });
  };



  const handleTabChange = (value: number) => {
    setCurrentTab(value);
  };

  const handleClickBack = () => {
    if (doClose) {
      doClose()
        .then((resp) => {
          if (resp.success) {
            navigate('/admin/dynamic_forms');
          }
        })
        .catch((result) => {
          snackbarContext.showNotification(result.error, 'error');
        });
    }
  }


  const handleUpdate = () => {
    // TODO:
  }

  const handleDelete = () => {
    // TODO:
  }

  return (
    <Card>
      <CardHeading>
        <Toolbar variant="dense" disableGutters>
          {/* TODO <FormAvatar ariaLabel="model" icon={}/> */}
          <FormHeading
            header={title}
            subheaderContent={<FormHeadingID id={recordId} />}
            dataChanged={dataChanged}
          />
          <FormFillContent />
          <CardToolbarButtonBack onClick={handleClickBack} />
          <CardToolbarMenuButton>
            <CardToolbarMenuItemDelete onClick={handleDelete} />
          </CardToolbarMenuButton>
        </Toolbar>
      </CardHeading>
      <CardContent style={{ paddingTop: 0 }}>
        <FormTabs
          value={currentTab}
          onChange={handleTabChange}
          identifier={dc.getSource()}
        >
          <FormTab
            id="basic"
            label={t('admin:common.tabs.basic')}
            icon={<BusinessCenterIcon />}
            aria-label="basic"
            value={0}
          />
          <FormTab
            id="licences"
            label={t('admin:model.tabs.fields')}
            icon={<DataArrayIcon />}
            aria-label="fields"
            value={1}
          />
        </FormTabs>
        <FormTabPanel value={currentTab} index={0}>
          <GridContainer direction="row" spacing={3}>
            <GridItem>
              <GridContainer direction="row">
                <FormContent
                  title={t('admin:common.sections.basic') as string}
                  fieldNames={[
                    'title', 'title_ttoken', 'mw_procedure_name', 'app_permission_id'
                  ]}
                  record={record}
                  validation={validation}
                  onFieldChange={onFieldChange}
                  fields={fields}
                  mode={mode}
                  pickerDataControllers={pickerDataControllers}
                  columns={3}
                />
              </GridContainer>
            </GridItem>
          </GridContainer>
        </FormTabPanel>
        <FormTabPanel value={currentTab} index={1}>
          <ModelTable
            title={t('admin:model.sections.fields') as string}
            key="tblid-model-admin-fields"
            dc={dcDynamicFormFields}
            records={dynamicFormFieldsRecords}
            allowSelection="one"
            allowRowAction={() => true}
            onRowAction={handleRowActionField}
            viewName="model_admin"
            allowColumnPicker
            allowAdd
            allowFilter
            handleAdd={openAddDialogField}
            addLabel={t('buttons.new_n') as string}
          />
        </FormTabPanel>
      </CardContent>
      <CardActions>
        <FormFillContent />
        <CardActionButtonSave variant="contained" onClick={handleUpdate} disabled={!dataChanged}/>
      </CardActions>
    </Card>
  )
}

export default DynamicFormForm;